import { useCallback, useEffect, useState } from "react";
import { Wallet, Plus, Trash2, Eye, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  listWatchedAddresses, addWatchedAddress, removeWatchedAddress, errorMessage,
} from "@/api/entities";
import { Panel, Heading, Pill, HudButton, Empty, accent } from "@/components/hud";

/* Read-only wallet watching.
   Everything here deals in PUBLIC addresses only. Connecting MetaMask or Phantom
   asks the extension for the account's public address and nothing else — we never
   request a signature, never see a private key or seed phrase, and the server has
   no code path that can move funds. Worst case, we display a balance. */

const CHAINS = [
  { chain: "eth", label: "Ethereum", symbol: "ETH", placeholder: "0x…" },
  { chain: "sol", label: "Solana", symbol: "SOL", placeholder: "Base58 address" },
  { chain: "btc", label: "Bitcoin", symbol: "BTC", placeholder: "bc1… or 1…" },
];

const CHAIN_TONE = { eth: "cyan", sol: "magenta", btc: "amber" };

export default function WalletWatcher() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chain, setChain] = useState("eth");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await listWatchedAddresses();
      setRows(res.addresses || []);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load watched addresses."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, successMsg) => {
    setBusy(true);
    try {
      await addWatchedAddress(payload);
      toast.success(successMsg);
      setAddress("");
      setLabel("");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not add that address."));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!address.trim()) return;
    save(
      { chain, address: address.trim(), label: label.trim() || undefined, origin: "manual" },
      "Address added — its balance will appear in Treasury.",
    );
  };

  const onRemove = async (row) => {
    try {
      await removeWatchedAddress(row.id);
      toast.success("Stopped watching that address.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove that address."));
    }
  };

  /* MetaMask: ask the extension which account is selected. eth_requestAccounts
     returns a public address only — it is not a transaction and grants us nothing
     beyond the ability to look the address up on a public explorer. */
  const connectMetaMask = async () => {
    const eth = window.ethereum;
    if (!eth) {
      toast.error("MetaMask isn't installed in this browser.");
      return;
    }
    setConnecting("metamask");
    try {
      const accounts = await eth.request({ method: "eth_requestAccounts" });
      const addr = (accounts || [])[0];
      if (!addr) {
        toast.error("MetaMask didn't share an account.");
        return;
      }
      await save({ chain: "eth", address: addr, label: "MetaMask", origin: "metamask" },
        "MetaMask connected — watching that address read-only.");
    } catch (e) {
      toast.error(e?.message || "MetaMask connection was rejected.");
    } finally {
      setConnecting("");
    }
  };

  /* Phantom: connect() resolves with the wallet's public key. Same story — a
     public address, no signature requested, no spending permission implied. */
  const connectPhantom = async () => {
    const provider = window.phantom?.solana || window.solana;
    if (!provider?.isPhantom) {
      toast.error("Phantom isn't installed in this browser.");
      return;
    }
    setConnecting("phantom");
    try {
      const res = await provider.connect();
      const addr = res?.publicKey?.toString?.() || provider.publicKey?.toString?.();
      if (!addr) {
        toast.error("Phantom didn't share an address.");
        return;
      }
      await save({ chain: "sol", address: addr, label: "Phantom", origin: "phantom" },
        "Phantom connected — watching that address read-only.");
    } catch (e) {
      toast.error(e?.message || "Phantom connection was rejected.");
    } finally {
      setConnecting("");
    }
  };

  const inputStyle = {
    border: "1px solid var(--yb-hairline)",
    color: "var(--yb-text)",
    background: "transparent",
  };

  return (
    <Panel>
      <Heading
        title="Watched wallets"
        kicker="read-only · public addresses"
        right={<Pill tone="mint">view only</Pill>}
      />

      <p className="text-[12px] mb-4 leading-relaxed" style={{ color: "var(--yb-text-muted)" }}>
        Add a public wallet address and its live balance shows up under Verified Holdings
        in Treasury, priced in AUD. This is a look-up only: the app never asks for a private
        key or seed phrase, never requests a signature, and has no way to move your coins.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        <HudButton type="button" variant="ghost" onClick={connectMetaMask} disabled={busy || !!connecting}>
          {connecting === "metamask" ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
          Connect MetaMask
        </HudButton>
        <HudButton type="button" variant="ghost" tone="magenta" onClick={connectPhantom} disabled={busy || !!connecting}>
          {connecting === "phantom" ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
          Connect Phantom
        </HudButton>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-[110px_1fr_150px_auto] gap-2 mb-5">
        <select
          value={chain}
          onChange={(e) => setChain(e.target.value)}
          aria-label="Chain"
          className="h-10 px-2 rounded text-[13px] outline-none"
          style={inputStyle}
        >
          {CHAINS.map((c) => (
            <option key={c.chain} value={c.chain} style={{ background: "var(--yb-panel)" }}>
              {c.symbol}
            </option>
          ))}
        </select>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          aria-label="Public address"
          placeholder={CHAINS.find((c) => c.chain === chain)?.placeholder}
          className="h-10 px-3 rounded font-mono text-[12px] outline-none"
          style={inputStyle}
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          aria-label="Label"
          placeholder="Label (optional)"
          className="h-10 px-3 rounded text-[13px] outline-none"
          style={inputStyle}
        />
        <HudButton type="submit" disabled={busy || !address.trim()}>
          <Plus size={14} /> Watch
        </HudButton>
      </form>

      {loading ? (
        <div className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>Loading addresses…</div>
      ) : rows.length === 0 ? (
        <Empty
          icon={Eye}
          title="No wallets watched yet"
          hint="Connect a wallet or paste a public address above to track its real balance."
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const tone = CHAIN_TONE[r.chain] || "cyan";
            const fromEnv = r.origin === "env";
            return (
              <li
                key={r.id || `${r.chain}:${r.address}`}
                className="flex items-center gap-3 p-3 rounded"
                style={{ border: "1px solid var(--yb-hairline)" }}
              >
                <span
                  className="font-mono text-[10px] px-2 py-1 rounded shrink-0"
                  style={{ border: `1px solid ${accent(tone).c}`, color: accent(tone).c }}
                >
                  {r.chain.toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate" style={{ color: "var(--yb-text)" }}>
                    {r.label}
                    {r.origin && r.origin !== "manual" && (
                      <span className="ml-2 text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
                        via {r.origin}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] truncate" style={{ color: "var(--yb-text-muted)" }}>
                    {r.address}
                  </div>
                </div>
                {fromEnv ? (
                  <span
                    className="text-[11px] flex items-center gap-1 shrink-0"
                    style={{ color: "var(--yb-text-muted)" }}
                    title="Set by a server environment variable — remove it there."
                  >
                    <AlertTriangle size={12} /> from server
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onRemove(r)}
                    aria-label={`Stop watching ${r.label}`}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5 shrink-0"
                    style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
